import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useSession, Terminal, Equipo } from '../../../../core/context/SessionContext';
import logoCoordinadora from '../../../../assets/images/logo-coordinadora.png';
import { VentaApiService } from '../../../venta/infrastructure/api/VentaApiService';

export const SelectTerminalPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { setSessionData } = useSession();
  
  const [terminalesData, setTerminalesData] = useState<Terminal[]>([]);
  const [isLoadingTerminales, setIsLoadingTerminales] = useState(true);
  
  const [equiposData, setEquiposData] = useState<Equipo[]>([]);
  const [isLoadingEquipos, setIsLoadingEquipos] = useState(false);
  
  const [selectedTerminalId, setSelectedTerminalId] = useState<string>('');
  const [selectedEquipoId, setSelectedEquipoId] = useState<string>('');
  const [errorTerminales, setErrorTerminales] = useState<string | null>(null);
  const [errorEquipos, setErrorEquipos] = useState<string | null>(null);

  // Cargar terminales desde el backend
  useEffect(() => {
    const cargarTerminales = async () => {
      try {
        if (auth.user?.access_token) {
          const terminales = await VentaApiService.obtenerTerminalesBD(auth.user.access_token);
          setTerminalesData(terminales);
        }
      } catch (error: unknown) {
        const detail = error instanceof Error ? error.message : 'Error desconocido';
        setErrorTerminales(`No se pudieron cargar las terminales: ${detail}`);
      } finally {
        setIsLoadingTerminales(false);
      }
    };

    if (auth.isAuthenticated) {
      cargarTerminales();
    }
  }, [auth.isAuthenticated, auth.user?.access_token]);

  // Si no está autenticado y no está cargando, lo devolvemos al login
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      navigate('/login');
    }
  }, [auth.isLoading, auth.isAuthenticated, navigate]);

  const keycloakData = auth.user?.profile;

  const userId = keycloakData?.sub ?? keycloakData?.preferred_username ?? 'default';
  const EQUIPO_PREF_KEY = `droop_equipo_pref_${userId}`;

  const isLocal = process.env.REACT_APP_ENV === 'local';

  const terminalDesdeToken = !isLocal &&
    !!keycloakData?.terminal &&
    terminalesData.some(t => t.codigo_terminal.toString() === String(keycloakData.terminal));

  useEffect(() => {
    if (keycloakData?.terminal && !selectedTerminalId && terminalesData.length > 0) {
      const terminal = keycloakData.terminal;
      const terminalTokenStr = typeof terminal === 'string' || typeof terminal === 'number'
        ? String(terminal)
        : JSON.stringify(terminal);
      const existeTerminal = terminalesData.some(
        t => t.codigo_terminal.toString() === terminalTokenStr
      );
      if (existeTerminal) {
        setSelectedTerminalId(terminalTokenStr);
      }
    }
  }, [keycloakData, selectedTerminalId, terminalesData]);

  // Cargar equipos cuando se selecciona una terminal
  useEffect(() => {
    const cargarEquipos = async () => {
      if (!selectedTerminalId) {
        setEquiposData([]);
        return;
      }
      
      setIsLoadingEquipos(true);
      try {
        const equipos = await VentaApiService.obtenerEquiposBD(Number.parseInt(selectedTerminalId, 10), auth.user?.access_token ?? '');
        setEquiposData(equipos);
      } catch (error: unknown) {
        const detail = error instanceof Error ? error.message : 'Error desconocido';
        setErrorEquipos(`No se pudieron cargar los equipos: ${detail}`);
      } finally {
        setIsLoadingEquipos(false);
      }
    };

    cargarEquipos();
  }, [selectedTerminalId, auth.user?.access_token]);

  // Pre-seleccionar equipo desde preferencia guardada en localStorage
  useEffect(() => {
    if (equiposData.length === 0) return;
    const saved = localStorage.getItem(EQUIPO_PREF_KEY);
    if (saved && equiposData.some(e => e.codigo_equipo.toString() === saved)) {
      setSelectedEquipoId(saved);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equiposData]);

  const handleTerminalChange = (event: SelectChangeEvent) => {
    setSelectedTerminalId(event.target.value);
    setSelectedEquipoId(''); // Resetear equipo al cambiar de terminal
  };

  const handleEquipoChange = (event: SelectChangeEvent) => {
    setSelectedEquipoId(event.target.value);
  };

  const handleContinuar = () => {
    const terminal = terminalesData.find(t => t.codigo_terminal.toString() === selectedTerminalId);
    const equipo = equiposData.find(e => e.codigo_equipo.toString() === selectedEquipoId);

    if (terminal && equipo) {
      localStorage.setItem(EQUIPO_PREF_KEY, selectedEquipoId);
      setSessionData(terminal, equipo);
      navigate(terminal.codigo_terminal === 10 ? '/historial' : '/venta');
    }
  };

  if (auth.isLoading || isLoadingTerminales) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>{auth.isLoading ? 'Validando credenciales...' : 'Cargando terminales...'}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 5,
          width: '100%',
          maxWidth: 450,
          borderRadius: 3,
          textAlign: 'center',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <img 
            src={logoCoordinadora} 
            alt="Coordinadora Logo" 
            style={{ maxWidth: '200px', height: 'auto' }} 
          />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
          Configuración de Sesión
        </Typography>
        
        <Typography variant="body2" sx={{ color: '#666', mb: 4 }}>
          Por favor, selecciona la terminal y el equipo en el que vas a operar.
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="terminal-label">Terminal</InputLabel>
          <Select
            labelId="terminal-label"
            value={selectedTerminalId}
            label="Terminal"
            onChange={handleTerminalChange}
            sx={{ textAlign: 'left' }}
            disabled={terminalesData.length === 0 || terminalDesdeToken}
          >
            {terminalesData.map((terminal) => (
              <MenuItem key={terminal.codigo_terminal} value={terminal.codigo_terminal.toString()}>
                {terminal.nombre_terminal} (Cód: {terminal.codigo_terminal})
              </MenuItem>
            ))}
          </Select>
          {errorTerminales && <Typography color="error" variant="caption" sx={{ mt: 1 }}>{errorTerminales}</Typography>}
        </FormControl>

        <FormControl fullWidth sx={{ mb: 4 }} disabled={!selectedTerminalId || equiposData.length === 0 || isLoadingEquipos}>
          <InputLabel id="equipo-label">Equipo</InputLabel>
          <Select
            labelId="equipo-label"
            value={selectedEquipoId}
            label="Equipo"
            onChange={handleEquipoChange}
            sx={{ textAlign: 'left' }}
          >
            {equiposData.map((equipo) => (
              <MenuItem key={equipo.codigo_equipo} value={equipo.codigo_equipo.toString()}>
                {equipo.nombre_equipo}
              </MenuItem>
            ))}
          </Select>
          {errorEquipos && <Typography color="error" variant="caption" sx={{ mt: 1 }}>{errorEquipos}</Typography>}
        </FormControl>

        <Button
          variant="contained"
          fullWidth
          size="large"
          color="secondary"
          disabled={!selectedTerminalId || !selectedEquipoId}
          onClick={handleContinuar}
          sx={{ py: 1.5, fontWeight: 'bold', borderRadius: 8 }}
        >
          Ingresar al POS
        </Button>
      </Paper>
    </Box>
  );
};
